import React, { useState, useEffect, useRef } from 'react';
import {
  MdSwapHoriz, MdAdd, MdFileDownload, MdPictureAsPdf, MdUpload,
  MdSearch, MdClose, MdHistory, MdLocationOn, MdFilterList,
  MdArrowForward, MdCheckCircle, MdCancel,
} from 'react-icons/md';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useChangeStream } from '../lib/useChangeStream';
import { downloadFromApi } from '../lib/fileDownload';
import { tsFilename } from '../lib/filename';
import { prompt } from '../lib/dialogs';
import SearchableSelect from '../components/SearchableSelect';

const STATUS_BADGE = {
  approved: { bg: '#e8f5e9', fg: '#2e7d32', label: 'approved' },
  pending:  { bg: '#fff3e0', fg: '#ef6c00', label: 'pending'  },
  rejected: { bg: '#ffebee', fg: '#c62828', label: 'rejected' },
};

const Toast = ({ msg, type, onClose }) => (
  <div className={`toast toast-${type}`} style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
    {msg}
    <button onClick={onClose} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 16 }}>×</button>
  </div>
);

const Modal = ({ title, onClose, children }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
      <div className="modal-header">
        <span className="modal-title">{title}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}><MdClose /></button>
      </div>
      {children}
    </div>
  </div>
);

const AssetMovement = () => {
  const { user } = useAuth();
  const isApprover = user?.role === 'admin' || user?.role === 'manager';

  const [movements, setMovements] = useState([]);
  const [assets, setAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({ search: '', from_location: '', to_location: '', status: '', start_date: '', end_date: '' });
  const [form, setForm] = useState({ asset_id: '', to_location_id: '', reason_id: '', notes: '' });
  const [assetSearch, setAssetSearch] = useState('');
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const importRef = useRef();

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchMovements = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.from_location) params.from_location = filters.from_location;
      if (filters.to_location) params.to_location = filters.to_location;
      if (filters.status) params.status = filters.status;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const res = await api.get('/movements', { params });
      setMovements(res.data);
    } catch { showToast('Failed to load movements', 'error'); }
    setLoading(false);
  };

  const fetchReasons = async () => {
    try {
      const r = await api.get('/movement-reasons?active=true');
      setReasons(r.data);
    } catch {}
  };

  const handleApprove = async (m) => {
    try {
      await api.post(`/movements/${m.id}/approve`);
      showToast(`Movement #${m.id} approved`);
      fetchMovements();
      fetchAssets();
    } catch (err) {
      showToast(err.response?.data?.error || 'Approve failed', 'error');
    }
  };
  const handleReject = async (m) => {
    const reason = await prompt('Rejection reason (optional):') ?? '';
    if (reason === null) return;
    try {
      await api.post(`/movements/${m.id}/reject`, { reason });
      showToast(`Movement #${m.id} rejected`);
      fetchMovements();
    } catch (err) {
      showToast(err.response?.data?.error || 'Reject failed', 'error');
    }
  };

  // The move picker needs a workable list, not the whole register: this used
  // to pull every asset (231 MB for a 145k-asset company) to populate a
  // dropdown. The cap is well above any list a human picks from; operators
  // with more than this search instead.
  const MOVE_PICKER_LIMIT = 2000;
  const fetchAssets = async () => {
    try {
      const res = await api.get('/assets', { params: { limit: MOVE_PICKER_LIMIT } });
      setAssets(res.data || []);
    } catch (err) {
      console.warn('[movement] asset list failed', err);
      setAssets([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await api.get('/locations');
      setLocations(res.data);
    } catch {}
  };

  useEffect(() => { fetchMovements(); fetchAssets(); fetchLocations(); fetchReasons(); }, []);
  useChangeStream(() => { fetchMovements(); fetchAssets(); }, ['movements', 'assets']);

  useEffect(() => {
    if (!assetSearch.trim()) { setFilteredAssets([]); return; }
    const q = assetSearch.toLowerCase();
    setFilteredAssets(assets.filter(a =>
      a.asset_name.toLowerCase().includes(q) ||
      (a.asset_tag || '').toLowerCase().includes(q) ||
      (a.rfid_tag || '').toLowerCase().includes(q) ||
      (a.serial_number || '').toLowerCase().includes(q)
    ).slice(0, 10));
  }, [assetSearch, assets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.asset_id || !form.to_location_id) {
      showToast('Please select an asset and destination location', 'error'); return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/movements', {
        ...form,
        reason_id: form.reason_id ? Number(form.reason_id) : null,
      });
      showToast(r.data.status === 'pending'
        ? 'Movement submitted — awaiting approval'
        : 'Asset moved successfully');
      setShowModal(false);
      setForm({ asset_id: '', to_location_id: '', reason_id: '', notes: '' });
      setSelectedAsset(null);
      setAssetSearch('');
      fetchMovements();
      fetchAssets();
    } catch (err) {
      showToast(err.response?.data?.error || 'Move failed', 'error');
    }
    setSubmitting(false);
  };

  const handleExport = async (type) => {
    try {
      const ext = type === 'excel' ? 'xlsx' : 'pdf';
      await downloadFromApi(`/movements/export/${type}`, tsFilename('asset-movements', ext));
    } catch (err) {
      showToast(err.message || 'Export failed', 'error');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await api.post('/movements/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { inserted = 0, errorCount = 0 } = res.data || {};
      const summary = inserted ? `${inserted} added` : 'no changes';
      showToast(
        `Import complete · ${summary}${errorCount ? ` · ${errorCount} row(s) skipped` : ''}`,
        errorCount && !inserted ? 'error' : 'success',
      );
      await fetchMovements();
      // Movements modify assets too — refresh that list so current locations
      // reflect the imported moves.
      if (typeof fetchAssets === 'function') await fetchAssets();
    } catch (err) {
      showToast(err.response?.data?.error || 'Import failed', 'error');
    }
    e.target.value = '';
  };

  const locationName = (id) => locations.find(l => l.id === id)?.location_name || '—';

  const pendingCount = movements.filter(m => m.status === 'pending').length;
  const stats = [
    { label: 'Total Movements', value: movements.length, color: 'var(--primary)' },
    { label: 'Pending Approval', value: pendingCount, color: '#ef6c00' },
    { label: 'Today', value: movements.filter(m => m.movement_date && m.movement_date.startsWith(new Date().toISOString().slice(0, 10))).length, color: 'var(--accent)' },
    { label: 'Unique Assets', value: new Set(movements.map(m => m.asset_id)).size, color: 'var(--success)' },
  ];

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title"><MdSwapHoriz /> Asset Movement</div>
          <div className="page-subtitle">Track and log asset relocations between locations</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={() => importRef.current.click()}>
            <MdUpload /> Import Excel
          </button>
          <input ref={importRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />
          <button className="btn btn-success btn-sm" onClick={() => handleExport('excel')}><MdFileDownload /> Excel</button>
          <button className="btn btn-danger btn-sm" onClick={() => handleExport('pdf')}><MdPictureAsPdf /> PDF</button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <MdAdd /> Move Asset
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', marginBottom: 20 }}>
        {stats.map(s => (
          <div key={s.label} className="stat-card">
            <div>
              <div className="stat-value" style={{ color: s.color, fontSize: '1.6rem' }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
          <MdFilterList style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontWeight: 600, fontSize: 13 }}>Filters</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          <div className="search-bar">
            <MdSearch />
            <input placeholder="Search asset…" value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
          </div>
          <SearchableSelect className="form-control" value={filters.from_location}
            onChange={e => setFilters(f => ({ ...f, from_location: e.target.value }))}>
            <option value="">All From Locations</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.location_name}</option>)}
          </SearchableSelect>
          <SearchableSelect className="form-control" value={filters.to_location}
            onChange={e => setFilters(f => ({ ...f, to_location: e.target.value }))}>
            <option value="">All To Locations</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.location_name}</option>)}
          </SearchableSelect>
          <SearchableSelect className="form-control" value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">All Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </SearchableSelect>
          <input type="date" className="form-control" value={filters.start_date}
            onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))} />
          <input type="date" className="form-control" value={filters.end_date}
            onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-primary btn-sm" onClick={fetchMovements} disabled={loading} style={{ flex: 1 }}>
              {loading ? '…' : 'Apply'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => {
              setFilters({ search: '', from_location: '', to_location: '', start_date: '', end_date: '' });
              setTimeout(fetchMovements, 50);
            }}>Clear</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-header">
          <div className="card-title"><MdHistory style={{ color: 'var(--accent)' }} /> Movement History</div>
          <span className="badge badge-info">{movements.length} records</span>
        </div>
        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Asset</th>
                  <th>RFID Tag</th>
                  <th>From</th>
                  <th></th>
                  <th>To</th>
                  <th>Status</th>
                  <th>Moved By</th>
                  <th>Reason</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 ? (
                  <tr>
                    <td colSpan={11} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                      <div className="empty-state">
                        <MdSwapHoriz style={{ fontSize: '3rem', opacity: 0.3 }} />
                        <h3>No movements recorded</h3>
                        <p>Click "Move Asset" to start tracking asset relocations</p>
                      </div>
                    </td>
                  </tr>
                ) : movements.map((m, i) => {
                  const sb = STATUS_BADGE[m.status] || STATUS_BADGE.approved;
                  return (
                    <tr key={m.id}>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{m.asset_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.asset_tag}</div>
                      </td>
                      <td><code style={{ fontSize: 11, color: 'var(--accent)' }}>{m.rfid_tag || '—'}</code></td>
                      <td>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MdLocationOn style={{ color: 'var(--warning)', fontSize: 14 }} />
                          {m.from_location_name || '—'}
                        </span>
                      </td>
                      <td><MdArrowForward style={{ color: 'var(--text-muted)' }} /></td>
                      <td>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MdLocationOn style={{ color: 'var(--success)', fontSize: 14 }} />
                          <strong>{m.to_location_name || '—'}</strong>
                        </span>
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                          background: sb.bg, color: sb.fg, fontSize: 11, fontWeight: 600,
                          textTransform: 'uppercase', letterSpacing: 0.4,
                        }}>{sb.label}</span>
                        {m.approved_by_name && m.status !== 'pending' && (
                          <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>by {m.approved_by_name}</div>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>{m.moved_by_name || '—'}</td>
                      <td>
                        {m.reason_name || m.reason ? <span className="badge badge-secondary">{m.reason_name || m.reason}</span> : '—'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {m.movement_date ? new Date(m.movement_date).toLocaleString() : '—'}
                      </td>
                      <td>
                        {m.status === 'pending' && isApprover ? (
                          <div className="action-buttons">
                            <button className="btn btn-success btn-icon btn-sm" title="Approve" onClick={() => handleApprove(m)}>
                              <MdCheckCircle />
                            </button>
                            <button className="btn btn-danger btn-icon btn-sm" title="Reject" onClick={() => handleReject(m)}>
                              <MdCancel />
                            </button>
                          </div>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Move Asset Modal */}
      {showModal && (
        <Modal title="Move Asset" onClose={() => { setShowModal(false); setSelectedAsset(null); setAssetSearch(''); }}>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* Asset Search */}
              <div className="form-group">
                <label className="form-label">Asset *</label>
                {selectedAsset ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', background: '#e8f5e9', borderRadius: 8,
                    border: '1.5px solid #4CAF50'
                  }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{selectedAsset.asset_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {selectedAsset.asset_tag} · {selectedAsset.rfid_tag}
                        <span className="badge badge-secondary" style={{ marginLeft: 8 }}>
                          Current: {selectedAsset.location_name || 'Unknown'}
                        </span>
                      </div>
                    </div>
                    <button type="button" onClick={() => { setSelectedAsset(null); setAssetSearch(''); setForm(f => ({ ...f, asset_id: '' })); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}>
                      <MdClose />
                    </button>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <div className="search-bar">
                      <MdSearch />
                      <input placeholder="Search by name, tag, RFID, serial…"
                        value={assetSearch} onChange={e => setAssetSearch(e.target.value)} autoFocus />
                    </div>
                    {filteredAssets.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                        background: 'white', border: '1.5px solid var(--border)',
                        borderRadius: 8, boxShadow: 'var(--shadow-lg)', maxHeight: 220, overflowY: 'auto'
                      }}>
                        {filteredAssets.map(a => (
                          <div key={a.id} onClick={() => {
                            setSelectedAsset(a);
                            setForm(f => ({ ...f, asset_id: a.id }));
                            setAssetSearch('');
                            setFilteredAssets([]);
                          }} style={{
                            padding: '10px 14px', cursor: 'pointer',
                            borderBottom: '1px solid var(--border-light)',
                          }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f7f9fc'}
                            onMouseLeave={e => e.currentTarget.style.background = 'white'}
                          >
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{a.asset_name}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              {a.asset_tag} · {a.rfid_tag} · <span style={{ color: 'var(--warning)' }}>{a.location_name || 'No location'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* To Location */}
              <div className="form-group">
                <label className="form-label">Destination Location *</label>
                <SearchableSelect className="form-control" value={form.to_location_id}
                  onChange={e => setForm(f => ({ ...f, to_location_id: e.target.value }))} required>
                  <option value="">Select destination…</option>
                  {locations.filter(l => !selectedAsset || l.id !== selectedAsset.location_id).map(l => (
                    <option key={l.id} value={l.id}>{l.location_name} ({l.location_code})</option>
                  ))}
                </SearchableSelect>
              </div>

              {/* Reason */}
              <div className="form-group">
                <label className="form-label">Reason for Movement</label>
                <SearchableSelect className="form-control" value={form.reason_id}
                  onChange={e => setForm(f => ({ ...f, reason_id: e.target.value }))}>
                  <option value="">Select reason…</option>
                  {reasons.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.name}{r.requires_approval ? ' (requires approval)' : ''}
                    </option>
                  ))}
                </SearchableSelect>
                {reasons.length === 0 && (
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                    No reasons defined yet. Admins can add them in Settings → Movement Reasons.
                  </div>
                )}
                {form.reason_id && reasons.find(r => r.id === Number(form.reason_id))?.requires_approval && (
                  <div style={{ fontSize: 11, color: '#ef6c00', marginTop: 4 }}>
                    ⚠ This reason requires manager approval. The asset will not be moved until approved.
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} placeholder="Optional notes…"
                  value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => { setShowModal(false); setSelectedAsset(null); setAssetSearch(''); }}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Moving…' : <><MdSwapHoriz /> Confirm Move</>}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default AssetMovement;
